<script setup>
/**
 * CustomerSnapshotPanel.vue -- Unified identity + value snapshot block for the Customer Intel sidebar.
 *
 * Props:
 *   customer          (Object, optional)  -- id, name, firstname, lastname, email, group_id, created_at
 *   stats             (Object, optional)  -- recent_order_count, recent_spend, formatted_recent_spend
 *   identityConfidence (String, optional) -- 'Confirmed'|'Likely'|'Possible'|'Weak'
 *   rfmSegment        (String, optional)  -- 'Champion'|'Loyal'|'At Risk'|'Hibernating'|'Lost'
 *   prechatSummary    (String, optional)  -- compact pre-chat lead string
 *   lastActive        (String, optional)  -- last seen date string
 *   hasActiveCart     (Boolean)           -- show cart indicator dot (default: false)
 *   vehicleSummary    (String, optional)  -- e.g. "2005 Subaru WRX STI"
 *
 * Self-contained: does NOT import ConfidenceBadge or any other local component.
 * Design: read-only. Parent controls visibility via v-if.
 * Style: mirrors MvpCustomerIntel.vue token usage.
 */
import { computed } from 'vue';

const props = defineProps({
  customer: {
    type: Object,
    default: null,
  },
  stats: {
    type: Object,
    default: null,
  },
  identityConfidence: {
    type: String,
    default: null,
  },
  rfmSegment: {
    type: String,
    default: null,
  },
  prechatSummary: {
    type: String,
    default: null,
  },
  lastActive: {
    type: String,
    default: null,
  },
  hasActiveCart: {
    type: Boolean,
    default: false,
  },
  vehicleSummary: {
    type: String,
    default: null,
  },
});

// Derived display name (prefer combined first+last, fall back to name field)
const displayName = computed(() => {
  if (!props.customer) return '';
  const first = props.customer.firstname || '';
  const last = props.customer.lastname || '';
  if (first || last) return `${first} ${last}`.trim();
  return props.customer.name || '';
});

const displayEmail = computed(() => props.customer?.email || '');
const displayGroup = computed(() => props.customer?.group_id != null ? `Group ${props.customer.group_id}` : '');

// Customer "since" formatted as Mon YYYY
const customerSince = computed(() => {
  const d = props.customer?.created_at;
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
});

// Identity confidence badge styles (inline, self-contained)
const confidenceBadgeClass = computed(() => {
  switch ((props.identityConfidence || '').toLowerCase()) {
    case 'confirmed': return 'bg-n-teal-5 text-n-teal-12';
    case 'likely':    return 'bg-n-teal-5 text-n-teal-11';
    case 'possible':  return 'bg-n-amber-5 text-n-amber-12';
    case 'weak':      return 'bg-n-ruby-5 text-n-ruby-11';
    default:          return 'bg-n-solid-3 text-n-slate-10';
  }
});

// RFM segment badge styles
const rfmBadgeClass = computed(() => {
  switch ((props.rfmSegment || '').toLowerCase()) {
    case 'champion':    return 'bg-n-teal-5 text-n-teal-12';
    case 'loyal':       return 'bg-n-teal-5 text-n-teal-11';
    case 'at risk':     return 'bg-n-amber-5 text-n-amber-12';
    case 'hibernating': return 'bg-n-solid-3 text-n-slate-11';
    case 'lost':        return 'bg-n-ruby-5 text-n-ruby-11';
    default:            return 'bg-n-solid-3 text-n-slate-10';
  }
});

// Relative last-active time
const lastActiveRelative = computed(() => {
  const str = props.lastActive;
  if (!str) return '';
  try {
    const diff = Date.now() - new Date(str).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const mos = Math.floor(days / 30);
    return `${mos}mo ago`;
  } catch {
    return str;
  }
});

// Whether we have anything at all to render (guard for parent convenience)
const hasAnyContent = computed(() =>
  !!(displayName.value || displayEmail.value || props.stats || props.identityConfidence ||
     props.rfmSegment || props.prechatSummary || props.lastActive || props.hasActiveCart ||
     props.vehicleSummary)
);
</script>

<template>
  <div v-if="hasAnyContent" class="customer-snapshot-panel space-y-1.5">

    <!-- ── Identity block ─────────────────────────────────────── -->
    <div class="space-y-1">

      <!-- Row 1: name + badge row -->
      <div class="flex items-start justify-between gap-2 min-w-0">
        <div class="min-w-0 flex-1">
          <!-- Name -->
          <div
            v-if="displayName"
            class="text-sm font-semibold text-n-slate-12 leading-snug truncate"
          >{{ displayName }}</div>

          <!-- Email -->
          <div
            v-if="displayEmail"
            class="text-[11px] text-n-slate-10 truncate"
          >{{ displayEmail }}</div>
        </div>

        <!-- Badges column (confidence + RFM) -->
        <div class="flex flex-col items-end gap-1 shrink-0">
          <!-- Identity confidence badge -->
          <span
            v-if="identityConfidence"
            :class="confidenceBadgeClass"
            class="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide whitespace-nowrap"
          >{{ identityConfidence }}</span>

          <!-- RFM segment badge -->
          <span
            v-if="rfmSegment"
            :class="rfmBadgeClass"
            class="px-1.5 py-0.5 rounded text-[9px] font-medium capitalize whitespace-nowrap"
          >{{ rfmSegment }}</span>
        </div>
      </div>

      <!-- Row 2: group + customer-since -->
      <div v-if="displayGroup || customerSince" class="flex items-center gap-2 text-[10px] text-n-slate-9">
        <span v-if="displayGroup">{{ displayGroup }}</span>
        <span v-if="displayGroup && customerSince" class="text-n-slate-7">·</span>
        <span v-if="customerSince">since {{ customerSince }}</span>
      </div>

    </div>

    <!-- ── Stats row (spend + orders) ────────────────────────── -->
    <div
      v-if="stats && (stats.formatted_recent_spend || stats.recent_order_count)"
      class="flex items-baseline gap-2"
    >
      <span
        v-if="stats.formatted_recent_spend"
        class="text-base font-bold text-n-slate-12 leading-snug"
      >{{ stats.formatted_recent_spend }}</span>
      <span
        v-if="stats.recent_order_count"
        class="text-[10px] text-n-slate-10"
      >{{ stats.recent_order_count }} order{{ stats.recent_order_count !== 1 ? 's' : '' }}</span>
    </div>

    <!-- ── Vehicle summary ────────────────────────────────────── -->
    <div
      v-if="vehicleSummary"
      class="flex items-center gap-1.5 text-xs"
    >
      <i class="i-lucide-car text-n-slate-9 shrink-0" />
      <span class="text-n-slate-11 font-medium truncate">{{ vehicleSummary }}</span>
    </div>

    <!-- ── Last active + cart indicator ──────────────────────── -->
    <div
      v-if="lastActive || hasActiveCart"
      class="flex items-center gap-2 text-[10px] text-n-slate-9"
    >
      <span v-if="lastActive" class="flex items-center gap-1">
        <i class="i-lucide-clock text-n-slate-8 shrink-0" style="font-size:10px" />
        {{ lastActiveRelative || lastActive }}
      </span>
      <!-- Active cart dot -->
      <span
        v-if="hasActiveCart"
        class="flex items-center gap-1 text-n-amber-11 font-medium"
      >
        <span class="inline-block w-1.5 h-1.5 rounded-full bg-n-amber-9" />
        cart active
      </span>
    </div>

    <!-- ── Pre-chat compact summary row ──────────────────────── -->
    <div
      v-if="prechatSummary"
      class="flex items-start gap-1 text-[10px] text-n-slate-10 bg-n-solid-2 rounded px-2 py-1"
    >
      <i class="i-lucide-message-square text-n-slate-8 shrink-0 mt-px" style="font-size:10px" />
      <span class="leading-snug">{{ prechatSummary }}</span>
    </div>

  </div>
</template>

<style scoped>
.customer-snapshot-panel {
  font-size: 12px;
}
</style>
